import { PrescriberCoverageNotice } from "./PrescriberCoverage";
import { PendingDoseChangeHolds } from "./PendingDoseChangeHolds";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  ListChecks,
  RefreshCw,
  Search,
  Video,
} from "lucide-react";

import { Button } from "@/design-system/primitives";
import {
  useClinicianWorklist,
  useClinicianWorklistItem,
  useClinicianAppointmentPresence,
  useClinicianSession,
  useCompleteClinicianTask,
  useJoinClinicianAppointment,
  useRecordClinicianAppointmentAttendance,
  useRefreshClinicianWorklist,
  useRescheduleClinicianAppointment,
} from "@/lib/api/clinicianHooks";
import type {
  AppointmentPresence,
  AppointmentWorkItem,
  HealthieTaskWorkItem,
  PrescriptionStage,
} from "@/types/clinician";
import { PrescribingWorkspace } from "./PrescribingWorkspace";
import { OwnerOptions, useOwnerFilter } from "./ownerFilter";
import { PostVisitReviewModal } from "./PostVisitReviewModal";
import { nextUpcomingAppointments } from "./appointmentSelection";
import {
  attendanceIsFinal,
  shouldMonitorAppointmentPresence,
} from "./appointmentPresence";
import {
  formatTaskCreated,
  matchesTaskSearch,
  matchesWorklistSearch,
  sortPrescriptionTasks,
} from "./worklistSearch";

type View = "prescribing" | "visits" | "tasks";
type PrescriptionFilter =
  | "all"
  | "support_escalation"
  | "healthie_rx_task"
  | PrescriptionStage;

const FILTERS: { value: PrescriptionFilter; label: string }[] = [
  { value: "ready_to_prescribe", label: "Ready" },
  { value: "healthie_rx_task", label: "Rx tasks" },
  { value: "approval_required", label: "Approval" },
  { value: "sync_required", label: "Visit required" },
  { value: "visit_scheduled", label: "Visit scheduled" },
  // Paid orders parked on the new-patient ID gate. The backend only sends
  // counts.awaiting_id once it lists these rows, so the chip is hidden
  // against an older backend instead of showing an empty filter.
  { value: "awaiting_id", label: "Missing ID" },
  { value: "intake_required", label: "Check-in / intake missing" },
  { value: "prescribed", label: "Approved / prescribed" },
  { value: "rejected", label: "Rejected" },
  { value: "missing_info", label: "Missing info" },
  { value: "support_escalation", label: "Support escalations" },
  { value: "all", label: "All" },
];

const MINUTE_MS = 60_000;

function patientName(item: {
  patient: { display_name: string | null; email: string | null };
}) {
  return item.patient.display_name || item.patient.email || "Unknown patient";
}

export function WorklistScreen() {
  const { prescriptionId } = useParams();
  const routedPrescription = useRef<string | null>(null);
  const [routeParams, setRouteParams] = useSearchParams();
  const session = useClinicianSession();
  // Supervisors who prescribe start on their own patients (team routing).
  const owner = useOwnerFilter({ allowUnassigned: true });
  const selectedClinician = owner.clinicianId;
  const requestedView = routeParams.get("view");
  const routeView: View =
    requestedView === "visits" || requestedView === "tasks"
      ? requestedView
      : "prescribing";
  const [view, setView] = useState<View>(routeView);
  const [filter, setFilter] = useState<PrescriptionFilter>(
    prescriptionId ? "all" : "ready_to_prescribe",
  );
  const [search, setSearch] = useState("");
  const normalizedSearch = search.trim();
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [reviewAppointment, setReviewAppointment] =
    useState<AppointmentWorkItem | null>(null);
  const pageSize = 100;
  const refreshWorklist = useRefreshClinicianWorklist();
  const showingRxTasks = filter === "healthie_rx_task";
  // The Rx-task chip is a client-side view over the tasks array that every
  // worklist response already carries. Keep the queue request on the default
  // stage so the backend never receives a stage it does not classify.
  const requestStage = showingRxTasks ? "ready_to_prescribe" : filter;
  const worklist = useClinicianWorklist(selectedClinician, {
    stage: requestStage,
    limit: pageSize,
    offset: page * pageSize,
    enabled: owner.ready && !normalizedSearch,
  });
  const searchWorklist = useClinicianWorklist(selectedClinician, {
    stage: requestStage,
    search: debouncedSearch || undefined,
    limit: pageSize,
    offset: page * pageSize,
    enabled: owner.ready && Boolean(normalizedSearch && debouncedSearch),
  });
  const activeData = normalizedSearch
    ? (searchWorklist.data ?? worklist.data)
    : worklist.data;
  const pagePrescriptions = activeData?.prescriptions ?? [];
  const selectedIsLoaded = Boolean(
    prescriptionId &&
    pagePrescriptions.some((item) => item.id === prescriptionId),
  );
  // A linked prescription opens whoever owns it (supervisors see all; others
  // are scoped by the backend), even when the list shows one owner.
  const linkedPrescription = useClinicianWorklistItem(
    undefined,
    prescriptionId ?? null,
    owner.ready && Boolean(prescriptionId && !selectedIsLoaded),
  );
  const loadedPrescriptions = useMemo(() => {
    const linked = linkedPrescription.data;
    if (!linked || pagePrescriptions.some((item) => item.id === linked.id)) {
      return pagePrescriptions;
    }
    return [...pagePrescriptions, linked];
  }, [linkedPrescription.data, pagePrescriptions]);
  const prescriptions = useMemo(
    () =>
      loadedPrescriptions.filter((item) => matchesWorklistSearch(item, search)),
    [loadedPrescriptions, search],
  );
  const counts = activeData?.counts;
  const warnings = activeData?.warnings ?? [];
  const appointmentsUnavailable = warnings.includes("appointments_unavailable");
  const tasksUnavailable = warnings.includes("tasks_unavailable");
  const showingRecentData = warnings.some((warning) =>
    ["worklist_refreshing", "appointments_stale", "tasks_stale"].includes(
      warning,
    ),
  );
  const allHealthiePrescriptionTasks = useMemo(
    () =>
      sortPrescriptionTasks(
        (activeData?.tasks ?? []).filter((task) => task.kind === "prescription"),
      ),
    [activeData?.tasks],
  );
  const healthiePrescriptionTasks = useMemo(
    () =>
      allHealthiePrescriptionTasks.filter((task) =>
        matchesTaskSearch(task, search),
      ),
    [allHealthiePrescriptionTasks, search],
  );
  const generalTasks = (activeData?.tasks ?? []).filter(
    (task) => task.kind !== "prescription",
  );
  const prescriptionCount = counts
    ? counts.ready_to_prescribe +
      counts.approval_required +
      counts.intake_required +
      counts.sync_required +
      counts.visit_scheduled +
      counts.prescribed +
      counts.rejected +
      counts.missing_info +
      (counts.awaiting_id ?? 0) +
      (counts.healthie_prescription_tasks ?? 0)
    : 0;
  const filteredTotal = normalizedSearch
    ? (searchWorklist.data?.prescription_total ?? prescriptions.length)
    : (worklist.data?.prescription_total ?? 0);
  const pageStart = filteredTotal ? page * pageSize + 1 : 0;
  const pageEnd = Math.min((page + 1) * pageSize, filteredTotal);
  const searchingAll = Boolean(
    normalizedSearch &&
    (normalizedSearch !== debouncedSearch ||
      searchWorklist.isFetching ||
      !searchWorklist.data),
  );

  useEffect(() => setPage(0), [filter, selectedClinician]);
  useEffect(() => {
    if (!normalizedSearch) {
      setDebouncedSearch("");
      return;
    }
    const timeout = window.setTimeout(
      () => setDebouncedSearch(normalizedSearch),
      300,
    );
    return () => window.clearTimeout(timeout);
  }, [normalizedSearch]);
  const tabs: { value: View; label: string; count: number | string }[] = [
    {
      value: "prescribing",
      label: "Prescribing",
      count: tasksUnavailable ? `${prescriptionCount}+` : prescriptionCount,
    },
    {
      value: "visits",
      label: "Visits",
      count: appointmentsUnavailable ? "—" : (counts?.visits ?? 0),
    },
    {
      value: "tasks",
      label: "Other tasks",
      count: tasksUnavailable ? "—" : (counts?.tasks ?? 0),
    },
  ];

  useEffect(() => setView(routeView), [routeView]);

  useEffect(() => {
    if (!prescriptionId || !loadedPrescriptions.length) return;
    if (routedPrescription.current === prescriptionId) return;
    routedPrescription.current = prescriptionId;
    const requested = loadedPrescriptions.find(
      (item) => item.id === prescriptionId,
    );
    if (!requested) return;
    setFilter(requested.stage);
  }, [loadedPrescriptions, prescriptionId]);

  const selectView = (next: View) => {
    setView(next);
    const updated = new URLSearchParams(routeParams);
    if (next === "prescribing") updated.delete("view");
    else updated.set("view", next);
    setRouteParams(updated, { replace: true });
  };

  return (
    <div className="max-w-[120rem] mx-auto px-12 md:px-24 xl:px-40 py-24 md:py-40 flex flex-col gap-20">
      <AppointmentPresenceMonitor
        appointments={worklist.data?.appointments ?? []}
      />
      <PrescriberCoverageNotice
        enabled={Boolean(session.data?.capabilities?.manage_team)}
      />
      <header className="flex flex-wrap items-end justify-between gap-15">
        <div className="flex flex-col gap-6">
          <span className="type-label-sm text-ink-muted">CLINICIAN TODAY</span>
          <h1 className="type-heading-xl">Clinical worklist</h1>
          <p className="type-body-sm text-ink-muted max-w-[68rem]">
            Review the product-linked intake, chart, recent messages, visit,
            physician decision, and medication without leaving the queue.
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          loading={
            refreshWorklist.isPending ||
            worklist.isFetching ||
            searchWorklist.isFetching
          }
          onClick={() => refreshWorklist.mutate()}
        >
          <RefreshCw size={13} aria-hidden /> Refresh
        </Button>
        {owner.supervisor && (
          <label className="type-label-sm text-ink-muted">
            Owner
            <select
              className="ml-7 rounded border border-line bg-raised px-9 py-7 text-ink"
              value={owner.selected}
              onChange={(event) => owner.select(event.target.value)}
            >
              <OwnerOptions owner={owner} />
            </select>
          </label>
        )}
      </header>
      <PendingDoseChangeHolds />

      {counts && (
        <div className="grid grid-cols-2 lg:grid-cols-8 border border-line-subtle bg-raised divide-x divide-y lg:divide-y-0 divide-line-subtle">
          <Metric
            label="Ready"
            value={counts.ready_to_prescribe}
            tone="success"
          />
          <Metric
            label="Approval"
            value={counts.approval_required}
            tone="danger"
          />
          <Metric label="Sync" value={counts.sync_required} tone="warning" />
          <Metric
            label="Visits"
            value={appointmentsUnavailable ? "—" : counts.visits}
          />
          <Metric
            label="Prescribed · all time"
            value={counts.prescribed_total ?? counts.prescribed}
            tone="success"
          />
          <Metric
            label="Missing info"
            value={counts.missing_info}
            tone="warning"
          />
          <Metric
            label="Support escalations"
            value={counts.support_escalations ?? 0}
            tone="warning"
          />
          <Metric
            label="Other tasks"
            value={tasksUnavailable ? "—" : counts.tasks}
          />
        </div>
      )}

      <NextAppointmentsStrip
        appointments={worklist.data?.appointments ?? []}
        canAttest={Boolean(session.data?.capabilities?.prescribe)}
        onReviewVisit={setReviewAppointment}
        timezone={
          session.data?.timezone ??
          Intl.DateTimeFormat().resolvedOptions().timeZone
        }
      />

      {warnings.length ? (
        <div
          role="status"
          className="border border-warning/30 bg-warning-wash text-warning px-12 py-8 type-body-sm"
        >
          {showingRecentData && (
            <p>
              Healthie is refreshing. Showing recent cached worklist data while
              live updates load.
            </p>
          )}
          {(appointmentsUnavailable || tasksUnavailable) && (
            <p>
              Some{" "}
              {appointmentsUnavailable && tasksUnavailable
                ? "visits and tasks"
                : appointmentsUnavailable
                  ? "visits"
                  : "tasks"}{" "}
              could not be loaded. Their totals are unavailable, not zero.
              Available records remain visible. Updates retry automatically; you
              can also refresh.
            </p>
          )}
          {!showingRecentData &&
            !appointmentsUnavailable &&
            !tasksUnavailable && (
              <p>
                Some live Healthie data could not be loaded. Refresh to retry.
              </p>
            )}
        </div>
      ) : null}

      <div className="flex border-b border-line-subtle" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={view === tab.value}
            onClick={() => selectView(tab.value)}
            className={`px-12 py-9 type-label-sm border-b-2 -mb-px focus-visible:outline-none focus-visible:shadow-focus ${
              view === tab.value
                ? "border-ink text-ink"
                : "border-transparent text-ink-muted hover:text-ink"
            }`}
          >
            {tab.label}{" "}
            <span className="ml-4 tabular-nums opacity-60">{tab.count}</span>
          </button>
        ))}
      </div>

      {!activeData && (worklist.isLoading || !owner.ready) ? (
        <WorklistSkeleton />
      ) : worklist.isError ? (
        <EmptyState
          icon={<AlertTriangle size={20} />}
          title="The clinical worklist could not be loaded"
          detail="Nothing was changed. Refresh to retry."
        />
      ) : view === "prescribing" ? (
        <>
          {!showingRxTasks && (
            <HealthiePrescriptionTasks
              tasks={healthiePrescriptionTasks}
              total={allHealthiePrescriptionTasks.length}
              searching={Boolean(normalizedSearch)}
            />
          )}
          <div className="flex flex-col gap-10">
            <div
              className="flex flex-wrap gap-6"
              role="group"
              aria-label="Prescription filters"
            >
              {FILTERS.filter(
                (entry) =>
                  entry.value !== "awaiting_id" ||
                  counts?.awaiting_id !== undefined,
              ).map((entry) => {
                const serverCount =
                  entry.value === "all"
                    ? prescriptionCount
                    : entry.value === "support_escalation"
                      ? (counts?.support_escalations ?? 0)
                      : entry.value === "healthie_rx_task"
                        ? (counts?.healthie_prescription_tasks ?? 0)
                        : (counts?.[entry.value] ?? 0);
                return (
                  <button
                    key={entry.value}
                    type="button"
                    aria-pressed={filter === entry.value}
                    onClick={() => setFilter(entry.value)}
                    className={`min-h-32 px-12 rounded-pill type-label-sm border focus-visible:outline-none focus-visible:shadow-focus ${
                      filter === entry.value
                        ? "bg-ink text-page border-ink"
                        : "border-line text-ink-muted hover:bg-sunken"
                    }`}
                  >
                    {entry.label}{" "}
                    {serverCount > 0 && (
                      <span className="ml-4 opacity-60">{serverCount}</span>
                    )}
                  </button>
                );
              })}
            </div>
            <label className="relative block max-w-[54rem]">
              <Search
                size={15}
                className="absolute left-11 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none"
                aria-hidden
              />
              <input
                type="search"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
                placeholder="Search patient, medication, order, or status"
                className="w-full min-h-40 pl-36 pr-12 border border-line bg-raised type-body-sm outline-none focus:shadow-focus"
              />
            </label>
            {showingRxTasks ? (
              <div className="flex flex-wrap items-center justify-between gap-8 type-label-sm text-ink-muted">
                <span>
                  {normalizedSearch
                    ? `Showing ${healthiePrescriptionTasks.length} of ${allHealthiePrescriptionTasks.length} Rx tasks`
                    : `Showing ${healthiePrescriptionTasks.length} Rx tasks`}
                </span>
              </div>
            ) : (normalizedSearch || filteredTotal > 0) && (
              <div className="flex flex-wrap items-center justify-between gap-8 type-label-sm text-ink-muted">
                <span>
                  {searchingAll
                    ? `${prescriptions.length} local match${prescriptions.length === 1 ? "" : "es"} · Searching full worklist…`
                    : normalizedSearch
                      ? `Showing ${pageStart}–${pageEnd} of ${filteredTotal} match${filteredTotal === 1 ? "" : "es"}`
                      : `Showing ${pageStart}–${pageEnd} of ${filteredTotal}`}
                </span>
                <div className="flex gap-6" aria-busy={searchingAll}>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={searchingAll || page === 0}
                    onClick={() => setPage((value) => Math.max(0, value - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={searchingAll || pageEnd >= filteredTotal}
                    onClick={() => setPage((value) => value + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
          {showingRxTasks ? (
            healthiePrescriptionTasks.length ? (
              <div className="border border-line-subtle bg-raised">
                <HealthieTaskRows tasks={healthiePrescriptionTasks} />
              </div>
            ) : tasksUnavailable && !allHealthiePrescriptionTasks.length ? (
              <EmptyState
                icon={<AlertTriangle size={20} />}
                title="Tasks unavailable"
                detail="Healthie did not return the task list. This does not mean there are no tasks. Refresh to retry."
              />
            ) : (
              <EmptyState
                icon={<ListChecks size={20} />}
                title={
                  normalizedSearch
                    ? "No Rx tasks match this search"
                    : "No Healthie prescription tasks"
                }
                detail="Manually-created dose change, reship, or recovery tasks assigned in Healthie appear here."
              />
            )
          ) : prescriptions.length ? (
            <PrescribingWorkspace items={prescriptions} />
          ) : searchingAll ? (
            <EmptyState
              icon={<Search size={20} />}
              title="Searching the full worklist"
              detail="Checking patients beyond the currently loaded page."
            />
          ) : searchWorklist.isError && normalizedSearch ? (
            <EmptyState
              icon={<AlertTriangle size={20} />}
              title="The full worklist search could not be completed"
              detail="Nothing was changed. Clear the search or try again."
            />
          ) : (
            <EmptyState
              icon={<CheckCircle2 size={20} />}
              title="No prescriptions in this view"
              detail="Change the status filter or search to review other clinical gates."
            />
          )}
        </>
      ) : view === "visits" ? (
        appointmentsUnavailable && !activeData?.appointments.length ? (
          <EmptyState
            icon={<AlertTriangle size={20} />}
            title="Visits unavailable"
            detail="Healthie did not return the calendar. This does not mean there are no visits. Refresh to retry."
          />
        ) : (
          <VisitList
            appointments={activeData?.appointments ?? []}
            canAttest={Boolean(session.data?.capabilities?.prescribe)}
            onReviewVisit={setReviewAppointment}
          />
        )
      ) : tasksUnavailable && !generalTasks.length ? (
        <EmptyState
          icon={<AlertTriangle size={20} />}
          title="Tasks unavailable"
          detail="Healthie did not return the task list. This does not mean there are no tasks. Refresh to retry."
        />
      ) : (
        <TaskList tasks={generalTasks} />
      )}
      {reviewAppointment && (
        <PostVisitReviewModal
          appointment={reviewAppointment}
          onClose={() => setReviewAppointment(null)}
        />
      )}
    </div>
  );
}

function HealthiePrescriptionTasks({
  tasks,
  total,
  searching,
}: {
  tasks: HealthieTaskWorkItem[];
  total: number;
  searching: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  if (!total) return null;
  if (searching && !tasks.length) return null;
  // A search already narrowed the list, so show every match instead of
  // hiding the one the clinician is looking for behind "View all".
  const collapsible = !searching && tasks.length > 3;
  const visibleTasks = collapsible && !expanded ? tasks.slice(0, 3) : tasks;
  return (
    <section className="border border-warning/30 bg-warning-wash">
      <div className="px-13 py-9 border-b border-warning/20 flex flex-wrap items-center justify-between gap-6">
        <div>
          <h2 className="type-label-sm text-warning">
            {searching
              ? `Healthie prescription tasks · ${tasks.length} of ${total} match`
              : `Healthie prescription tasks · ${total}`}
          </h2>
          <p className="text-11 text-ink-muted mt-2">
            Manually-created dose change, reship, or recovery work stays in
            Prescribing even when it is not linked to a new pharmacy order.
            Newest first.
          </p>
        </div>
        {collapsible && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            aria-expanded={expanded}
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? "Show fewer" : `View all ${tasks.length}`}
          </Button>
        )}
      </div>
      <HealthieTaskRows tasks={visibleTasks} tone="warning" />
    </section>
  );
}

function HealthieTaskRows({
  tasks,
  tone,
}: {
  tasks: HealthieTaskWorkItem[];
  tone?: "warning";
}) {
  const complete = useCompleteClinicianTask();
  const [confirming, setConfirming] = useState<string | null>(null);
  const divider =
    tone === "warning" ? "divide-warning/20" : "divide-line-subtle";
  return (
    <div className={`divide-y ${divider}`}>
      {tasks.map((task) => {
        const created = formatTaskCreated(task.created_at);
        return (
          <div
            key={task.id}
            className="p-13 grid lg:grid-cols-[minmax(13rem,1fr)_minmax(24rem,2fr)_auto] gap-10 items-center"
          >
            <div className="min-w-0">
              <p className="type-body-sm font-medium truncate">
                {patientName(task)}
              </p>
              <p className="text-11 text-ink-muted truncate">
                {task.patient.email}
              </p>
              {created && (
                <p
                  className={`text-11 mt-2 ${task.overdue ? "text-danger" : "text-ink-muted"}`}
                >
                  Created {created}
                  {task.overdue ? " · Overdue" : ""}
                </p>
              )}
            </div>
            <p className="type-body-sm whitespace-pre-wrap">{task.content}</p>
            <div className="flex flex-wrap gap-6 lg:justify-end">
              <Button asChild type="button" size="sm">
                <Link
                  to={
                    task.related_prescription_id
                      ? `/clinician-portal/today/${task.related_prescription_id}`
                      : `/clinician-portal/patients/${task.patient.id}`
                  }
                >
                  {task.related_prescription_id
                    ? "Review prescription"
                    : "Open patient chart"}
                </Link>
              </Button>
              <Button
                type="button"
                variant={confirming === task.id ? "primary" : "secondary"}
                size="sm"
                loading={complete.isPending && confirming === task.id}
                onClick={() => {
                  if (confirming !== task.id) return setConfirming(task.id);
                  complete.mutate(task.id, {
                    onSuccess: () => setConfirming(null),
                  });
                }}
              >
                {confirming === task.id ? "Confirm complete" : "Mark complete"}
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Metric({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | string;
  tone?: "success" | "danger" | "warning";
}) {
  const toneClass = tone
    ? {
        success: "text-success",
        danger: "text-danger",
        warning: "text-warning",
      }[tone]
    : "";
  return (
    <div className="p-12 flex flex-col gap-3">
      <span className="type-label-xs text-ink-muted">{label}</span>
      <span className={`type-heading-lg tabular-nums ${toneClass}`}>
        {value}
      </span>
    </div>
  );
}

function WorklistSkeleton() {
  return (
    <div className="border border-line-subtle divide-y divide-line-subtle bg-raised animate-pulse">
      {[0, 1, 2, 3].map((item) => (
        <div key={item} className="p-15 flex flex-col gap-8">
          <div className="h-14 w-1/3 bg-sunken" />
          <div className="h-12 w-2/3 bg-sunken" />
        </div>
      ))}
    </div>
  );
}

function NextAppointmentsStrip({
  appointments,
  timezone,
  canAttest,
  onReviewVisit,
}: {
  appointments: AppointmentWorkItem[];
  timezone: string;
  canAttest: boolean;
  onReviewVisit: (appointment: AppointmentWorkItem) => void;
}) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const interval = window.setInterval(() => setNow(new Date()), MINUTE_MS);
    return () => window.clearInterval(interval);
  }, []);
  const upcoming = nextUpcomingAppointments(appointments, now);
  if (!upcoming.length) return null;
  return (
    <section className="border border-line-subtle bg-raised">
      <div className="px-12 py-8 border-b border-line-subtle bg-sunken flex items-center justify-between gap-8">
        <div>
          <p className="type-label-sm font-medium">Next appointments</p>
          <p className="text-11 text-ink-muted">
            Up to three active/upcoming visits · {timezone.replaceAll("_", " ")}
          </p>
        </div>
        <Link
          to="/clinician-portal/today?view=visits"
          className="type-label-xs underline underline-offset-4"
        >
          All visits
        </Link>
      </div>
      <div className="divide-y divide-line-subtle">
        {upcoming.map((appointment) => (
          <VisitRow
            key={appointment.id}
            appointment={appointment}
            displayTimezone={timezone}
            canAttest={canAttest}
            onReviewVisit={onReviewVisit}
          />
        ))}
      </div>
    </section>
  );
}

function EmptyState({
  icon,
  title,
  detail,
}: {
  icon: ReactNode;
  title: string;
  detail: string;
}) {
  return (
    <div className="border border-line-subtle bg-raised p-40 text-center flex flex-col items-center gap-8">
      {icon}
      <p className="type-heading-sm">{title}</p>
      <p className="type-body-sm text-ink-muted">{detail}</p>
    </div>
  );
}

function VisitList({
  appointments,
  canAttest,
  onReviewVisit,
}: {
  appointments: AppointmentWorkItem[];
  canAttest: boolean;
  onReviewVisit: (appointment: AppointmentWorkItem) => void;
}) {
  const upcoming = appointments.filter(
    (appointment) => !appointment.joined_at && !isPastAppointment(appointment),
  );
  const recent = appointments
    .filter(
      (appointment) =>
        Boolean(appointment.joined_at) || isPastAppointment(appointment),
    )
    .sort(
      (left, right) => Date.parse(right.starts_at) - Date.parse(left.starts_at),
    );
  if (!upcoming.length && !recent.length) {
    return (
      <EmptyState
        icon={<CalendarClock size={20} />}
        title="No upcoming patient visits"
        detail="New Healthie appointments appear here automatically."
      />
    );
  }
  return (
    <div className="flex flex-col gap-12">
      {upcoming.length ? (
        <div className="border border-line-subtle bg-raised divide-y divide-line-subtle">
          {upcoming.map((appointment) => (
            <VisitRow
              key={appointment.id}
              appointment={appointment}
              canAttest={canAttest}
              onReviewVisit={onReviewVisit}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          icon={<CheckCircle2 size={20} />}
          title="No visits waiting for a clinician"
          detail="Recent visits are retained below for follow-up visibility."
        />
      )}
      {recent.length > 0 && (
        <details open className="border border-line-subtle bg-raised">
          <summary className="cursor-pointer px-14 py-10 type-label-sm text-ink-muted">
            RECENT VISITS · {recent.length}
          </summary>
          <div className="divide-y divide-line-subtle border-t border-line-subtle">
            {recent.map((appointment) => (
              <RecentVisitRow
                key={appointment.id}
                appointment={appointment}
                canAttest={canAttest}
                onReviewVisit={onReviewVisit}
              />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function VisitRow({
  appointment,
  displayTimezone,
  canAttest = false,
  onReviewVisit,
}: {
  appointment: AppointmentWorkItem;
  displayTimezone?: string;
  canAttest?: boolean;
  onReviewVisit: (appointment: AppointmentWorkItem) => void;
}) {
  const reschedule = useRescheduleClinicianAppointment();
  const join = useJoinClinicianAppointment();
  const [editing, setEditing] = useState(false);
  const [newTime, setNewTime] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const start = new Date(appointment.starts_at);
  const monitorPresence = shouldMonitorAppointmentPresence(appointment);
  const presence = useClinicianAppointmentPresence(
    appointment.id,
    appointment.patient.id,
    monitorPresence,
  );
  const submit = async () => {
    if (!newTime) return;
    setMessage(null);
    try {
      await reschedule.mutateAsync({
        appointmentId: appointment.id,
        patientId: appointment.patient.id,
        startsAt: new Date(newTime).toISOString(),
        timezone: appointment.timezone,
      });
      setMessage("Appointment rescheduled.");
      setEditing(false);
    } catch {
      setMessage(
        "Reschedule failed; the existing appointment was not changed.",
      );
    }
  };
  return (
    <div className="p-15 grid lg:grid-cols-[12rem_minmax(14rem,1fr)_minmax(12rem,1fr)_auto] gap-10 items-center">
      <div className="flex flex-col gap-2">
        <span className="type-body-sm font-medium">
          {start.toLocaleDateString([], {
            weekday: "short",
            month: "short",
            day: "numeric",
          })}
        </span>
        <span className="type-label-sm text-ink-muted">
          {formatTimeWithZone(start, displayTimezone)}
        </span>
      </div>
      <div className="min-w-0 flex flex-col gap-2">
        <span className="type-body-sm font-medium truncate">
          {patientName(appointment)}
        </span>
        <span className="text-11 text-ink-muted truncate">
          {appointment.patient.email}
        </span>
      </div>
      <div className="flex flex-col gap-2">
        <span className="type-body-sm">
          {appointment.appointment_type || "Clinical visit"}
        </span>
        <span className="text-11 text-ink-muted">
          {[appointment.status, patientLocalTime(appointment)]
            .filter(Boolean)
            .join(" · ")}
        </span>
        <VisitPresenceStatus
          appointment={appointment}
          presence={presence.data}
        />
      </div>
      <div className="flex flex-wrap gap-7 lg:justify-end">
        {appointment.join_url && (
          <Button asChild size="sm">
            <a
              href={appointment.join_url}
              target="_blank"
              rel="noreferrer"
              onClick={() =>
                join.mutate({
                  appointmentId: appointment.id,
                  patientId: appointment.patient.id,
                })
              }
            >
              <Video size={13} aria-hidden /> Join call
            </a>
          </Button>
        )}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setEditing((v) => !v)}
        >
          Reschedule
        </Button>
        <AttendanceActions
          appointment={appointment}
          presence={presence.data}
          canAttest={canAttest}
          onReviewVisit={onReviewVisit}
        />
      </div>
      {editing && (
        <div className="lg:col-start-2 lg:col-span-3 border-t border-line-subtle pt-10 flex flex-wrap items-end gap-8">
          <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
            New time (your computer timezone)
            <input
              type="datetime-local"
              value={newTime}
              onChange={(event) => setNewTime(event.target.value)}
              className="min-h-40 border border-line bg-raised px-10 type-body-sm text-ink outline-none focus:shadow-focus"
            />
          </label>
          <Button
            type="button"
            size="sm"
            loading={reschedule.isPending}
            disabled={!newTime}
            onClick={submit}
          >
            Confirm reschedule
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setEditing(false)}
          >
            Cancel
          </Button>
        </div>
      )}
      {message && (
        <p
          className={`lg:col-start-2 lg:col-span-3 type-body-sm ${message.startsWith("Reschedule failed") ? "text-danger" : "text-success"}`}
        >
          {message}
        </p>
      )}
    </div>
  );
}

function AppointmentPresenceMonitor({
  appointments,
}: {
  appointments: AppointmentWorkItem[];
}) {
  return (
    <>
      {appointments
        .filter((appointment) => shouldMonitorAppointmentPresence(appointment))
        .map((appointment) => (
          <AppointmentPresenceProbe
            key={appointment.id}
            appointment={appointment}
          />
        ))}
    </>
  );
}

function AppointmentPresenceProbe({
  appointment,
}: {
  appointment: AppointmentWorkItem;
}) {
  const queryClient = useQueryClient();
  const presence = useClinicianAppointmentPresence(
    appointment.id,
    appointment.patient.id,
  );

  useEffect(() => {
    if (
      presence.data?.meeting_started_at &&
      presence.data.meeting_started_at !== appointment.joined_at
    ) {
      void queryClient.invalidateQueries({
        queryKey: ["clinician", "worklist"],
      });
    }
  }, [appointment.joined_at, presence.data?.meeting_started_at, queryClient]);

  if (
    !presence.data ||
    attendanceIsFinal(presence.data.attendance_status) ||
    ["scheduled", "unavailable", "cancelled", "ended"].includes(
      presence.data.meeting_status,
    )
  ) {
    return null;
  }
  return (
    <div className="border border-warning/40 bg-warning-wash px-14 py-10 flex flex-wrap items-center justify-between gap-8">
      <div className="min-w-0 flex flex-col gap-2">
        <span className="type-label-xs text-warning">LIVE VISIT UPDATE</span>
        <span className="type-body-sm font-medium truncate">
          {patientName(appointment)}
        </span>
        <span className="text-11 text-ink-muted truncate">
          {[
            appointment.appointment_type || "Clinical visit",
            patientLocalTime(appointment),
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
        <VisitPresenceStatus
          appointment={appointment}
          presence={presence.data}
        />
      </div>
      <a
        href="/clinician-portal/today?view=visits"
        className="type-label-sm underline underline-offset-4"
      >
        Review visits
      </a>
    </div>
  );
}

function RecentVisitRow({
  appointment,
  canAttest,
  onReviewVisit,
}: {
  appointment: AppointmentWorkItem;
  canAttest: boolean;
  onReviewVisit: (appointment: AppointmentWorkItem) => void;
}) {
  const presence = useClinicianAppointmentPresence(
    appointment.id,
    appointment.patient.id,
    shouldMonitorAppointmentPresence(appointment),
  );
  const clinician = clinicianName(appointment, presence.data);
  const joinedAt = presence.data?.meeting_started_at || appointment.joined_at;
  const start = new Date(appointment.starts_at);
  const fallback = joinedAt
    ? `${clinician} started ${formatTime(joinedAt)}`
    : appointment.status || `Scheduled ${formatTimeWithZone(start)}`;

  return (
    <div className="px-14 py-10 flex flex-wrap items-center justify-between gap-8">
      <div className="flex min-w-0 flex-col gap-2">
        <span className="type-body-sm font-medium truncate">
          {patientName(appointment)}
        </span>
        <span className="text-11 text-ink-muted truncate">
          {[
            appointment.appointment_type || "Clinical visit",
            patientLocalTime(appointment),
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-7">
        <VisitPresenceStatus
          appointment={appointment}
          presence={presence.data}
          fallback={fallback}
        />
        <AttendanceActions
          appointment={appointment}
          presence={presence.data}
          canAttest={canAttest}
          onReviewVisit={onReviewVisit}
        />
      </div>
    </div>
  );
}

function AttendanceActions({
  appointment,
  presence,
  canAttest,
  onReviewVisit,
}: {
  appointment: AppointmentWorkItem;
  presence?: AppointmentPresence;
  canAttest: boolean;
  onReviewVisit: (appointment: AppointmentWorkItem) => void;
}) {
  const recordAttendance = useRecordClinicianAppointmentAttendance();
  const [confirming, setConfirming] = useState<"attended" | "no_show" | null>(
    null,
  );
  const [recorded, setRecorded] = useState<"attended" | "no_show" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const status =
    recorded || presence?.attendance_status || appointment.attendance_status;
  const appointmentStarted = Date.parse(appointment.starts_at) <= Date.now();

  if (!canAttest || !appointmentStarted) return null;
  if (status === "attended" || status === "no_show") {
    return (
      <div className="flex flex-wrap items-center justify-end gap-6">
        <span
          className={`type-label-xs ${status === "attended" ? "text-success" : "text-danger"}`}
        >
          {status === "attended" ? "Patient attended" : "Patient no-show"}
          {presence?.attendance_source === "manual" ||
          appointment.attendance_source === "manual" ||
          recorded
            ? " · recorded"
            : ""}
        </span>
        {status === "attended" && (
          <Button
            type="button"
            size="sm"
            onClick={() => onReviewVisit(appointment)}
          >
            {appointment.post_visit_review_status === "completed"
              ? "Review visit"
              : appointment.post_visit_review_status === "partially_completed"
                ? "Resume visit"
                : "Complete visit"}
          </Button>
        )}
      </div>
    );
  }

  const submit = async (attendanceStatus: "attended" | "no_show") => {
    setError(null);
    try {
      const result = await recordAttendance.mutateAsync({
        appointmentId: appointment.id,
        patientId: appointment.patient.id,
        attendanceStatus,
      });
      setRecorded(result.attendance_status);
      setConfirming(null);
      if (result.attendance_status === "attended") {
        onReviewVisit(appointment);
      }
    } catch (failure) {
      setError(
        failure instanceof Error
          ? failure.message
          : "Attendance was not recorded. Try again.",
      );
    }
  };

  if (confirming) {
    return (
      <div className="flex flex-wrap items-center justify-end gap-6">
        <span className="text-11 text-ink-muted">
          Confirm{" "}
          {confirming === "attended" ? "patient attended" : "patient no-show"}?
        </span>
        <Button
          type="button"
          size="sm"
          loading={recordAttendance.isPending}
          onClick={() => void submit(confirming)}
        >
          Confirm
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={recordAttendance.isPending}
          onClick={() => setConfirming(null)}
        >
          Cancel
        </Button>
        {error && (
          <span className="basis-full text-11 text-danger">{error}</span>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center justify-end gap-6">
      <Button
        type="button"
        size="sm"
        variant="secondary"
        onClick={() => setConfirming("attended")}
      >
        Patient attended
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={() => setConfirming("no_show")}
      >
        Patient no-show
      </Button>
    </div>
  );
}

function VisitPresenceStatus({
  appointment,
  presence,
  fallback = null,
}: {
  appointment: AppointmentWorkItem;
  presence?: AppointmentPresence;
  fallback?: string | null;
}) {
  const clinician = clinicianName(appointment, presence);
  if (!presence) {
    return fallback ? (
      <span className="type-label-xs text-success">{fallback}</span>
    ) : null;
  }

  const content: Partial<
    Record<AppointmentPresence["meeting_status"], [string, string]>
  > = {
    waiting_for_clinician: [
      `Waiting for ${clinician} to start`,
      "text-warning",
    ],
    clinician_late: [
      `${clinician} has not started · ${presence.clinician_late_minutes}m late`,
      "text-danger",
    ],
    clinician_missed: [
      `Possible missed visit · ${clinician} never started`,
      "text-danger",
    ],
    clinician_joined: [
      `${clinician} started${presence.meeting_started_at ? ` ${formatTime(presence.meeting_started_at)}` : ""} · Waiting room open`,
      "text-success",
    ],
    in_progress: [
      `Visit in progress${presence.participant_count ? ` · ${presence.participant_count} participants` : ""}`,
      "text-success",
    ],
    ended: ["Visit ended", "text-ink-muted"],
    cancelled: ["Visit cancelled", "text-ink-muted"],
  };
  const status = content[presence.meeting_status];
  if (!status) return null;
  return <span className={`type-label-xs ${status[1]}`}>{status[0]}</span>;
}

function clinicianName(
  appointment: AppointmentWorkItem,
  presence?: AppointmentPresence,
) {
  const clinician =
    presence?.joined_by ||
    appointment.joined_by ||
    appointment.assigned_clinician;
  return clinician?.display_name || clinician?.email || "Clinician";
}

function formatTime(value: string) {
  return new Date(value).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatTimeWithZone(value: Date, timezone?: string) {
  return value.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
    ...(timezone ? { timeZone: timezone } : {}),
  });
}

function patientLocalTime(appointment: AppointmentWorkItem) {
  if (!appointment.timezone) return null;
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (appointment.timezone === browserTimezone) return null;
  try {
    return `Patient ${new Date(appointment.starts_at).toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      timeZone: appointment.timezone,
      timeZoneName: "short",
    })}`;
  } catch {
    return `Patient timezone ${appointment.timezone}`;
  }
}

function isPastAppointment(appointment: AppointmentWorkItem) {
  const startsAt = Date.parse(appointment.starts_at);
  if (!Number.isFinite(startsAt)) return false;
  const parsedEnd = appointment.ends_at
    ? Date.parse(appointment.ends_at)
    : Number.NaN;
  const endsAt = Number.isFinite(parsedEnd)
    ? parsedEnd
    : startsAt + 30 * MINUTE_MS;
  return endsAt < Date.now();
}

function TaskList({ tasks }: { tasks: HealthieTaskWorkItem[] }) {
  const complete = useCompleteClinicianTask();
  const [confirming, setConfirming] = useState<string | null>(null);
  if (!tasks.length) {
    return (
      <EmptyState
        icon={<ListChecks size={20} />}
        title="No other patient-linked tasks"
        detail="Prescription work and support escalations are attached directly to patients in Prescribing."
      />
    );
  }
  return (
    <div className="border border-line-subtle bg-raised divide-y divide-line-subtle">
      {tasks.map((task) => (
        <div
          key={task.id}
          className="p-15 grid md:grid-cols-[minmax(14rem,1fr)_minmax(20rem,2fr)_10rem_auto] gap-10 items-center"
        >
          <div className="min-w-0 flex flex-col gap-2">
            <span className="type-body-sm font-medium truncate">
              {patientName(task)}
            </span>
            <span className="text-11 text-ink-muted truncate">
              {task.patient.email}
            </span>
          </div>
          <div className="flex flex-col gap-4">
            <p className="type-body-sm whitespace-pre-wrap">{task.content}</p>
          </div>
          <span
            className={`type-label-sm ${task.overdue ? "text-danger" : "text-ink-muted"}`}
          >
            {task.due_date
              ? `Due ${new Date(`${task.due_date}T00:00:00`).toLocaleDateString()}`
              : "No due date"}
          </span>
          <Button
            type="button"
            variant={confirming === task.id ? "primary" : "secondary"}
            size="sm"
            loading={complete.isPending && confirming === task.id}
            onClick={() => {
              if (confirming !== task.id) return setConfirming(task.id);
              complete.mutate(task.id, {
                onSuccess: () => setConfirming(null),
              });
            }}
          >
            {confirming === task.id ? "Confirm complete" : "Mark complete"}
          </Button>
        </div>
      ))}
    </div>
  );
}
